import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CostsSvg from 'images/chart.svg';
import CommentsSvg from 'images/comment.svg';
import SummarySvg from 'images/info-circle.svg';
import CheckIcon from 'images/v5/icons/check.svg';
import DotsIcon from 'images/v5/icons/dots-horizontal.svg';
import FileSvg from 'images/v5/icons/file-04.svg';
import FileAttachmentSvg from 'images/v5/icons/file-attachment-03.svg';
import QuantitiesSvg from 'images/v5/icons/file-check-01.svg';
import ConsumptionSvg from 'images/v5/icons/file-minus-02.svg';
import ComponentsSvg from 'images/v5/icons/layers-two-01.svg';
import ComponentsCostsSvg from 'images/v5/icons/receipt.svg';
import Rows01Svg from 'images/v5/icons/rows-01.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import StatusBadge from 'layout/atoms/StatusBadge';
import Subtitle from 'layout/atoms/Subtitle';
import TabsWizard from 'layout/atoms/TabsWizard/TabsWizard';
import Tooltip from 'layout/atoms/Tooltip/Tooltip';
import Grid from 'layout/molecules/Grid/Grid';
import Tools from 'layout/organisms/Tools';
import WizardSteps from 'layout/templates/WizardSteps';
import { closeAllSidebars, closeModal, openModal, selectModalByName } from 'store/layoutSlice';
import { resetSpecificationComponentsCosts } from 'store/specificationComponentsCostsSlice';
import { resetSpecificationComponentsState } from 'store/specificationComponentsSlice';
import { resetSpecificationCosts } from 'store/specificationCostsSlice';
import { fetchSpecificationDetails, fetchSpecificationProblems, requestStepValidation, resetSpecificationDetails, selectCreatorActiveTab, setBrandNewSpecState, setCreatorActiveTab, syncSpecificationVariants, } from 'store/specificationDetailsSlice';
import { resetSpecificationProductionQuantities } from 'store/specificationProductionQuantitiesSlice';
import { setRefetchFlag } from 'store/specificationsFoldersSlice';
import { setRefetchFlag as setMyFilesRefetchFlag } from 'store/specificationsSlice';
import { addToast } from 'store/toastsSlice';
import Comments from './Comments';
import ComponentConsumptions from './ComponentConsumptions';
import ComponentCosts from './ComponentCosts';
import Components from './Components';
import ComponentVariants from './ComponentVariants';
import EstimatedCosts from './EstimatedCosts';
import ProductionQuantities from './ProductionQuantities';
import Summary from './Summary';
function SpecificationDetails() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9;
    const { t } = useTranslation(['specification', 'common', 'emptyStates']);
    const dispatch = useDispatch();
    const modelSpecificationId = (_b = (_a = useSelector(selectModalByName('specificationCreator'))) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.id;
    const startActiveTab = (_d = (_c = useSelector(selectModalByName('specificationCreator'))) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.activeTab;
    const specificationDetails = useSelector((state) => state.specificationDetails);
    const creatorActiveTab = useSelector(selectCreatorActiveTab);
    const [activeTab, setActiveTab] = useState(startActiveTab || 'summary');
    const autoNavigated = useRef(false);
    const navigate = useNavigate();
    useEffect(() => {
        dispatch(fetchSpecificationDetails({ specificationId: modelSpecificationId }))
            .unwrap()
            .catch(() => {
            dispatch(closeModal({ name: 'specificationCreator' }));
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('emptyStates:SPECIFICATION_404_HEADER'),
                description: t('emptyStates:SPECIFICATION_404_DESCRIPTION'),
            }));
        });
        dispatch(closeAllSidebars());
        if (specificationDetails.refetchFlags.fetchProblems) {
            dispatch(fetchSpecificationProblems({ specificationId: modelSpecificationId }));
        }
    }, [
        t,
        dispatch,
        navigate,
        modelSpecificationId,
        specificationDetails.refetchFlags.fetchProblems,
    ]);
    useEffect(() => {
        return () => {
            dispatch(resetSpecificationDetails());
            dispatch(resetSpecificationProductionQuantities());
            dispatch(resetSpecificationComponentsState());
            dispatch(resetSpecificationComponentsCosts());
            dispatch(resetSpecificationCosts());
            dispatch(setBrandNewSpecState(false));
            dispatch(setCreatorActiveTab(null));
        };
    }, [dispatch]);
    useEffect(() => {
        if (creatorActiveTab) {
            setActiveTab(creatorActiveTab);
            dispatch(setCreatorActiveTab(null));
        }
    }, [creatorActiveTab, dispatch]);
    const problems = (_e = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _e === void 0 ? void 0 : _e.confirmationProblems;
    const calcProblems = (_f = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _f === void 0 ? void 0 : _f.estimatedCostCalculationProblems;
    const problemsLoaded = problems !== null && problems !== undefined;
    const hasNoComponents = problems === null || problems === void 0 ? void 0 : problems.some((p) => p.problemType === 'no_components');
    const tabs = [
        {
            id: 'summary',
            icon: React.createElement(SummarySvg, null),
            label: t('info'),
            error: false,
            completed: problemsLoaded &&
                !!((_g = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _g === void 0 ? void 0 : _g.fancyName) &&
                !!((_h = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _h === void 0 ? void 0 : _h.itemCode) &&
                !!((_j = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _j === void 0 ? void 0 : _j.brand) &&
                !!((_k = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _k === void 0 ? void 0 : _k.season) &&
                !!((_l = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _l === void 0 ? void 0 : _l.category),
        },
        {
            id: 'production-quantities',
            icon: React.createElement(QuantitiesSvg, null),
            label: t('production_quantities'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                problems &&
                problems.some((item) => item.problemType === 'zero_quantity'),
            completed: problemsLoaded &&
                ((_q = (_p = (_o = (_m = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _m === void 0 ? void 0 : _m.variants) === null || _o === void 0 ? void 0 : _o.edges) === null || _p === void 0 ? void 0 : _p.length) !== null && _q !== void 0 ? _q : 0) > 0 &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'zero_quantity')),
        },
        {
            id: 'components',
            icon: React.createElement(ComponentsSvg, null),
            label: t('components'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                problems &&
                problems.some((item) => item.problemType === 'no_components'),
            completed: problemsLoaded && !hasNoComponents,
        },
        {
            id: 'component-consumptions',
            icon: React.createElement(ConsumptionSvg, null),
            label: t('component_consumptions'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                problems &&
                problems.some((item) => item.problemType === 'no_component_consumption'),
            completed: problemsLoaded &&
                !hasNoComponents &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'no_component_consumption')),
        },
        {
            id: 'component-variants',
            icon: React.createElement(Rows01Svg, null),
            label: t('component_variants'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                problems &&
                (problems.some((item) => item.problemType === 'incomplete_variant') ||
                    problems.some((item) => item.problemType === 'incorrect_variant')),
            completed: problemsLoaded &&
                !hasNoComponents &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'incomplete_variant' || item.problemType === 'incorrect_variant')),
        },
        {
            id: 'component-costs',
            icon: React.createElement(ComponentsCostsSvg, null),
            label: t('component_costs'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                calcProblems &&
                calcProblems.some((item) => item.problemType === 'missing_variant_price'),
            completed: problemsLoaded &&
                !hasNoComponents &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'no_component_consumption')) &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'incomplete_variant' || item.problemType === 'incorrect_variant')) &&
                !(calcProblems === null || calcProblems === void 0 ? void 0 : calcProblems.some((item) => item.problemType === 'missing_variant_price')),
        },
        {
            id: 'estimated-costs',
            icon: React.createElement(CostsSvg, null),
            label: t('estimated_costs'),
            error: false,
            completed: problemsLoaded &&
                !hasNoComponents &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'no_component_consumption')) &&
                !(problems === null || problems === void 0 ? void 0 : problems.some((item) => item.problemType === 'incomplete_variant' || item.problemType === 'incorrect_variant')) &&
                !(calcProblems === null || calcProblems === void 0 ? void 0 : calcProblems.some((item) => item.problemType === 'missing_variant_price')),
        },
        {
            id: 'comments',
            icon: React.createElement(CommentsSvg, null),
            label: t('comments'),
            error: false,
            completed: ((_u = (_t = (_s = (_r = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _r === void 0 ? void 0 : _r.projections) === null || _s === void 0 ? void 0 : _s.edges) === null || _t === void 0 ? void 0 : _t.length) !== null && _u !== void 0 ? _u : 0) > 0,
        },
    ];
    useEffect(() => {
        if (problemsLoaded && !autoNavigated.current && !startActiveTab) {
            autoNavigated.current = true;
            const firstIncompleteTab = tabs.find((tab) => !tab.completed);
            setActiveTab(firstIncompleteTab ? firstIncompleteTab.id : tabs[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problemsLoaded]);
    const SKIPPABLE_TABS = new Set(['component-consumptions']);
    const STEP_ERROR_REVEAL_TABS = new Set([
        'summary',
        'production-quantities',
        'component-variants',
        'component-costs',
    ]);
    const currentTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const isFirstStep = currentTabIndex === 0;
    const isLastStep = currentTabIndex === tabs.length - 1;
    const handlePrevious = () => {
        if (currentTabIndex > 0) {
            setActiveTab(tabs[currentTabIndex - 1].id);
        }
    };
    const handleNext = () => {
        if (currentTabIndex < tabs.length - 1) {
            setActiveTab(tabs[currentTabIndex + 1].id);
        }
    };
    const handleFinish = () => {
        dispatch(setRefetchFlag({ flag: 'fetch', value: true }));
        dispatch(setMyFilesRefetchFlag({ flag: 'fetch', value: true }));
        dispatch(closeModal({ name: 'specificationCreator' }));
    };
    const renderNextButton = () => {
        var _a, _b;
        if (isLastStep) {
            return (React.createElement(Button, { color: "primary", onClick: handleFinish }, t('WIZARD_FINISH')));
        }
        if (!((_a = tabs[currentTabIndex]) === null || _a === void 0 ? void 0 : _a.completed) && SKIPPABLE_TABS.has(activeTab)) {
            return React.createElement(Button, { onClick: handleNext }, t('WIZARD_SKIP_STEP'));
        }
        if (!((_b = tabs[currentTabIndex]) === null || _b === void 0 ? void 0 : _b.completed)) {
            return (React.createElement(Tooltip, { theme: "inverse", maxWidth: 200, delay: 500, content: React.createElement(React.Fragment, null,
                    React.createElement("p", { style: { margin: '0 0 12px' } },
                        t('WIZARD_NEXT_DISABLED_TOOLTIP'),
                        ' ',
                        React.createElement("button", { type: "button", onClick: () => dispatch(openModal({ name: 'specificationProblems', data: { creator: true } })), style: {
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'inherit',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                            } }, t('WIZARD_NEXT_DISABLED_TOOLTIP_LINK'))),
                    STEP_ERROR_REVEAL_TABS.has(activeTab) && (React.createElement(Button, { size: "xs", onClick: () => dispatch(requestStepValidation()) }, t('show_errors')))) },
                React.createElement(Button, { display: "block", color: "primary", disabled: true }, t('WIZARD_NEXT_STEP'))));
        }
        return (React.createElement(Button, { color: "primary", onClick: handleNext }, t('WIZARD_NEXT_STEP')));
    };
    const handleApproveDraft = useCallback(() => dispatch(openModal({
        name: 'approveSpecification',
        data: { id: modelSpecificationId, creator: true },
    })), [dispatch, modelSpecificationId]);
    const handleSyncVariants = useCallback(() => {
        dispatch(syncSpecificationVariants({ specificationId: modelSpecificationId }))
            .unwrap()
            .then((result) => {
            dispatch(addToast({
                theme: 'ghost',
                color: 'positive',
                title: t('Variants synced'),
                description: t('Synchronized Variants: ') + result.createdVariants,
            }));
            dispatch(fetchSpecificationDetails({ specificationId: modelSpecificationId }));
            dispatch(fetchSpecificationProblems({ specificationId: modelSpecificationId }));
        })
            .catch(() => {
            dispatch(addToast({
                color: 'negative',
                title: t('Error'),
                description: t('Failed to sync variants'),
            }));
        });
    }, [dispatch, modelSpecificationId, t]);
    if (specificationDetails.status.fetch === 'succeeded') {
        return (React.createElement(Modal, { name: "specificationCreator", size: "fullScreen", header: React.createElement(Label, { weight: "strong" },
                React.createElement(FileSvg, null),
                " ",
                t('WIZARD_HEADER')), options: React.createElement(React.Fragment, null,
                (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.state) === 'DRAFT' && (React.createElement("span", { title: tabs.slice(0, -1).some((tab) => !tab.completed) ||
                        ((_v = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.confirmationProblems) === null || _v === void 0 ? void 0 : _v.length) > 0 ||
                        !((_w = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _w === void 0 ? void 0 : _w.productionTimeMinutes)
                        ? t('cannot_approve')
                        : undefined },
                    React.createElement(Button, { color: "primary", leftIcon: React.createElement(CheckIcon, null), onClick: handleApproveDraft, disabled: tabs.slice(0, -1).some((tab) => !tab.completed) ||
                            ((_x = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.confirmationProblems) === null || _x === void 0 ? void 0 : _x.length) > 0 ||
                            ((_z = (_y = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _y === void 0 ? void 0 : _y.estimatedCostCalculationProblems) === null || _z === void 0 ? void 0 : _z.blocksSpecificationConfirmation) ||
                            !((_0 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _0 === void 0 ? void 0 : _0.productionTimeMinutes), loading: specificationDetails.status.setToProduction === 'loading', title: tabs.slice(0, -1).some((tab) => !tab.completed) ||
                            ((_1 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.confirmationProblems) === null || _1 === void 0 ? void 0 : _1.length) > 0 ||
                            !((_2 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _2 === void 0 ? void 0 : _2.productionTimeMinutes)
                            ? t('cannot_approve')
                            : undefined }, t('approve_draft')))),
                React.createElement(Dropdown, { trigger: React.createElement(Button, { shape: "rounded", theme: "ghost", leftIcon: React.createElement(DotsIcon, null) }), testId: "specification-actions" },
                    React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(Rows01Svg, null), onClick: handleSyncVariants, loading: specificationDetails.status.syncVariants === 'loading' }, t('sync_variants')),
                    !!((_3 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _3 === void 0 ? void 0 : _3.fancyName) &&
                        !!((_4 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _4 === void 0 ? void 0 : _4.itemCode) &&
                        !!((_5 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _5 === void 0 ? void 0 : _5.brand) &&
                        !!((_6 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _6 === void 0 ? void 0 : _6.season) &&
                        !!((_7 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _7 === void 0 ? void 0 : _7.category) && (React.createElement(React.Fragment, null,
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(FileSvg, null), to: `/specifications/pdf/${specificationDetails.item.id}`, onClick: () => dispatch(closeModal({ name: 'specificationCreator' })), loading: specificationDetails.status.syncVariants === 'loading' }, t('GENERATE_PDF_BUTTON')),
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(FileAttachmentSvg, null), to: `/specifications/pdf-components/${specificationDetails.item.id}`, onClick: () => dispatch(closeModal({ name: 'specificationCreator' })), loading: specificationDetails.status.syncVariants === 'loading' }, t('GENERATE_PDF_WITH_COMPONENTS_BUTTON')))),
                    (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.state) === 'DRAFT' && (React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => dispatch(openModal({
                            name: 'removeSpecification',
                            data: {
                                id: modelSpecificationId,
                                itemCode: specificationDetails.item.itemCode,
                                creator: true,
                            },
                        })) }, t('delete_draft'))))), onClose: () => {
                dispatch(setRefetchFlag({ flag: 'fetch', value: true }));
                dispatch(setMyFilesRefetchFlag({ flag: 'fetch', value: true }));
            } },
            React.createElement(WizardSteps, { navigation: React.createElement(React.Fragment, null,
                    React.createElement("main", null,
                        ((_8 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.confirmationProblems) === null || _8 === void 0 ? void 0 : _8.length) > 0 ||
                            ((_9 = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.estimatedCostCalculationProblems) === null || _9 === void 0 ? void 0 : _9.length) > 0 ? (React.createElement(StatusBadge, { size: "l", theme: "fill", color: "negative", onClick: () => dispatch(openModal({ name: 'specificationProblems', data: { creator: true } })) }, t('specification_problems'))) : null,
                        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '2px' } },
                            React.createElement(Subtitle, { size: "s" }, specificationDetails.item.itemCode),
                            React.createElement(Label, { size: "xs", weight: "thin", color: "subtle" }, t('item_code'))),
                        React.createElement(TabsWizard, { activeTab: activeTab, tabs: tabs, onClick: (e) => setActiveTab(e) })),
                    React.createElement("footer", null,
                        React.createElement(Button, { theme: "ghost", onClick: handlePrevious, disabled: isFirstStep }, t('WIZARD_GO_BACK')),
                        renderNextButton())) },
                React.createElement(Grid, { vp: 12, hp: 24 },
                    activeTab === 'summary' && React.createElement(Summary, null),
                    activeTab === 'production-quantities' && React.createElement(ProductionQuantities, null),
                    activeTab === 'components' && React.createElement(Components, null),
                    activeTab === 'component-consumptions' && React.createElement(ComponentConsumptions, null),
                    activeTab === 'component-variants' && React.createElement(ComponentVariants, null),
                    activeTab === 'component-costs' && React.createElement(ComponentCosts, null),
                    activeTab === 'estimated-costs' && React.createElement(EstimatedCosts, null),
                    activeTab === 'comments' && React.createElement(Comments, { creator: true }))),
            React.createElement(Tools, { url: `/specifications/details/${specificationDetails.item.id}` })));
    }
    return (React.createElement(Modal, { name: "specificationCreator", size: "fullScreen", header: React.createElement(Label, { weight: "strong" },
            React.createElement(FileSvg, null),
            " ",
            t('WIZARD_HEADER')), onClose: () => {
            dispatch(setRefetchFlag({ flag: 'fetch', value: true }));
            dispatch(setMyFilesRefetchFlag({ flag: 'fetch', value: true }));
        } },
        React.createElement(Loader, null)));
}
export default memo(SpecificationDetails);
